// ═══════════════════════════════════════════════════════════════
// API DE NÓMINA - Backend Integration
// ═══════════════════════════════════════════════════════════════

import api from './config';

// ─────────────────────────────────────────────────────────────
// 1️⃣ GESTIÓN DE PERÍODOS DE NÓMINA
// ─────────────────────────────────────────────────────────────

/**
 * Crear nueva nómina
 */
export const createPayroll = async (data) => {
  const response = await api.post(`/payroll/create`, data);
  return response.data;
};

/**
 * Obtener nóminas de una empresa
 */
export const getPayrolls = async (companyId, filters = {}) => {
  const response = await api.get(`/payroll/list/${companyId}`, {
    params: filters
  });
  return response.data;
};

/**
 * Obtener detalle de una nómina
 */
export const getPayrollDetails = async (payrollId) => {
  const response = await api.get(`/payroll/${payrollId}`);
  return response.data;
};

/**
 * Validar si existe nómina duplicada
 */
export const validatePayrollDuplicate = async (companyId, periodo, quincena, tipo) => {
  const response = await api.post(`/payroll/validate-duplicate`, {
    companyId,
    periodo,
    quincena,
    tipo
  });
  return response.data;
};

// ─────────────────────────────────────────────────────────────
// 2️⃣ SELECCIÓN Y VALIDACIÓN DE EMPLEADOS
// ─────────────────────────────────────────────────────────────

/**
 * Obtener empleados elegibles para nómina
 */
export const getEligibleEmployees = async (companyId, periodo) => {
  const response = await api.get(`/payroll/eligible-employees/${companyId}`, {
    params: { periodo }
  });
  return response.data;
};

/**
 * Validar empleado para nómina
 */
export const validateEmployee = async (employeeId, periodo) => {
  const response = await api.post(`/payroll/validate-employee`, {
    employeeId,
    periodo
  });
  return response.data;
};

// ─────────────────────────────────────────────────────────────
// 3️⃣ PROCESAMIENTO DE NÓMINA
// ─────────────────────────────────────────────────────────────

/**
 * Simular nómina (preview)
 */
export const simulatePayroll = async (data) => {
  const response = await api.post(`/payroll/simulate`, data);
  return response.data;
};

/**
 * Procesar nómina completa
 */
export const processPayroll = async (payrollId, employeeIds) => {
  const response = await api.post(`/payroll/process/${payrollId}`, {
    employeeIds
  });
  return response.data;
};

/**
 * Verificar ingresos variables pendientes antes de procesar
 */
export const checkPendingIncomes = async (payrollId) => {
  const response = await api.get(`/payroll/${payrollId}/check-pending-incomes`);
  return response.data;
};

/**
 * Recalcular un empleado específico
 */
export const recalculateEmployee = async (payrollId, employeeId) => {
  const response = await api.post(`/payroll/recalculate`, {
    payrollId,
    employeeId
  });
  return response.data;
};

/**
 * Reprocesar todos los empleados de una nómina cerrada
 */
export const recalculatePayroll = async (payrollId, reason) => {
  const response = await api.post(`/payroll/${payrollId}/recalculate-all`, { reason });
  return response.data;
};

// ─────────────────────────────────────────────────────────────
// 4️⃣ GESTIÓN DE REGISTROS INDIVIDUALES
// ─────────────────────────────────────────────────────────────

/**
 * Obtener registros de nómina por empleado
 */
export const getEmployeePayrollRecords = async (payrollId, employeeId) => {
  const response = await api.get(`/payroll/${payrollId}/employee/${employeeId}`);
  return response.data;
};

/**
 * Actualizar manualmente un registro
 */
export const updatePayrollRecord = async (recordId, data) => {
  const response = await api.put(`/payroll/record/${recordId}`, data);
  return response.data;
};

// ─────────────────────────────────────────────────────────────
// 5️⃣ CIERRE Y CONTROL DE ESTADO
// ─────────────────────────────────────────────────────────────

/**
 * Cerrar nómina (bloquear edición)
 */
export const closePayroll = async (payrollId, userId) => {
  const response = await api.post(`/payroll/${payrollId}/close`, {
    userId
  });
  return response.data;
};

/**
 * Reabrir nómina cerrada
 */
export const reopenPayroll = async (payrollId, userId, reason) => {
  const response = await api.post(`/payroll/${payrollId}/reopen`, {
    userId,
    reason
  });
  return response.data;
};

/**
 * Anular nómina completa
 */
export const cancelPayroll = async (payrollId, userId, reason) => {
  const response = await api.post(`/payroll/${payrollId}/cancel`, {
    userId,
    reason
  });
  return response.data;
};

// ─────────────────────────────────────────────────────────────
// 6️⃣ REPORTES Y EXPORTACIÓN
// ─────────────────────────────────────────────────────────────

/**
 * Obtener resumen de nómina
 */
export const getPayrollSummary = async (payrollId) => {
  const response = await api.get(`/payroll/${payrollId}/summary`);
  return response.data;
};

/**
 * Exportar nómina a Excel
 */
export const exportPayrollToExcel = async (payrollId) => {
  const response = await api.get(`/payroll/${payrollId}/export/excel`, {
    responseType: 'blob'
  });
  return response.data;
};

/**
 * Exportar nómina a PDF
 */
export const exportPayrollToPDF = async (payrollId) => {
  const response = await api.get(`/payroll/${payrollId}/export/pdf`, {
    responseType: 'blob'
  });
  return response.data;
};

/**
 * Obtener resumen contable
 */
export const getAccountingSummary = async (payrollId) => {
  const response = await api.get(`/payroll/${payrollId}/accounting`);
  return response.data;
};

/**
 * Exportar resumen contable a Excel
 */
export const exportAccountingToExcel = async (payrollId) => {
  const response = await api.get(`/payroll/${payrollId}/accounting/excel`, {
    responseType: 'blob'
  });
  return response.data;
};

/**
 * Exportar resumen contable a PDF
 */
export const exportAccountingToPDF = async (payrollId) => {
  const response = await api.get(`/payroll/${payrollId}/accounting/pdf`, {
    responseType: 'blob'
  });
  return response.data;
};

// ─────────────────────────────────────────────────────────────
// 7️⃣ AUDITORÍA Y LOGS
// ─────────────────────────────────────────────────────────────

/**
 * Obtener historial de cambios
 */
export const getPayrollAuditLog = async (payrollId) => {
  const response = await api.get(`/payroll/${payrollId}/audit-log`);
  return response.data;
};

/**
 * Obtener logs de procesamiento
 */
export const getProcessingLogs = async (payrollId) => {
  const response = await api.get(`/payroll/${payrollId}/logs`);
  return response.data;
};

// ─────────────────────────────────────────────────────────────
// 8️⃣ COMPROBANTES Y DOCUMENTOS
// ─────────────────────────────────────────────────────────────

/**
 * Generar comprobante individual
 */
export const generatePayslip = async (payrollId, employeeId) => {
  const response = await api.get(
    `/payroll/${payrollId}/employee/${employeeId}/payslip`,
    { responseType: 'blob' }
  );
  return response.data;
};

/**
 * Generar comprobantes masivos (ZIP con todos los comprobantes)
 */
export const generateBulkPayslips = async (payrollId) => {
  const response = await api.post(
    `/payroll/${payrollId}/payslips/bulk`,
    {},
    { responseType: 'blob' }
  );
  return response.data;
};

/**
 * Exportar historial completo de comprobantes de un empleado (PDF multi-página)
 */
export const exportEmployeePayslipsHistory = async (employeeId) => {
  const response = await api.get(
    `/payroll/employee/${employeeId}/payslips/history`,
    { responseType: 'blob' }
  );
  return response.data;
};

// ─────────────────────────────────────────────────────────────
// 9️⃣ UTILITARIOS
// ─────────────────────────────────────────────────────────────

/**
 * Calcular diferencia entre nóminas
 */
export const comparePayrolls = async (payrollId1, payrollId2) => {
  const response = await api.post(`/payroll/compare`, {
    payrollId1,
    payrollId2
  });
  return response.data;
};

/**
 * Obtener estadísticas de nómina
 */
export const getPayrollStatistics = async (companyId, year) => {
  const response = await api.get(`/payroll/statistics/${companyId}`, {
    params: { year }
  });
  return response.data;
};

/**
 * ISR ya retenido en nóminas cerradas por empleado para un mes/año
 * Devuelve { [employeeId]: { isr_retenido_total, isr_q1, isr_q2, nominas_procesadas } }
 */
export const getIsrRetenido = async (companyId, mes, anio) => {
  const response = await api.get('/payroll/isr-retenido', {
    params: { companyId, mes, anio }
  });
  return response.data?.data || {};
};

export default {
  createPayroll,
  getPayrolls,
  getPayrollDetails,
  validatePayrollDuplicate,
  getEligibleEmployees,
  validateEmployee,
  simulatePayroll,
  processPayroll,
  recalculateEmployee,
  getEmployeePayrollRecords,
  updatePayrollRecord,
  closePayroll,
  reopenPayroll,
  cancelPayroll,
  getPayrollSummary,
  exportPayrollToExcel,
  exportPayrollToPDF,
  getAccountingSummary,
  exportAccountingToExcel,
  exportAccountingToPDF,
  getPayrollAuditLog,
  getProcessingLogs,
  generatePayslip,
  generateBulkPayslips,
  comparePayrolls,
  getPayrollStatistics,
  getIsrRetenido
};
